/**
 * Getting an invitee from "tapped a group invite link" to "in the group" when
 * the app wasn't installed yet (Sept 2026).
 *
 * A web page can't hand a device ID to the app, and matching people by IP /
 * device details is against Apple's rules (DPLA 3.3.3(B)). So the invite rides
 * across the install in ways the person starts themselves:
 *   - iPhone: the web invite page copies the invite link when they tap a store
 *     button; on first open the app offers "Paste invite" (one tap).
 *   - Android: the Play Store link carries referrer=join%3D<code> for the next
 *     Android build to read; until then, the same paste/code fallback.
 *   - Anyone can type the 8-character code.
 * Joining still goes through POST /api/invite/:code/accept, so expired or
 * revoked links fail the same way they always have.
 */
import { apiRequest, queryClient } from "@/lib/queryClient";

/** Both apps open links on this host (iOS universal links + Android app links). */
export const INVITE_ORIGIN = "https://spliiit.klarityit.ca";
const PENDING_KEY = "spliiit_pending_invite";

export function inviteUrl(code: string): string {
  return `${INVITE_ORIGIN}/#/invite/${code}`;
}

/** Pulls an invite code out of a pasted link, a ?join= link, or a bare code. */
export function extractInviteCode(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = text.match(/(?:#\/invite\/|[?&]join=)([A-Za-z0-9_-]{4,64})/);
  if (m) return m[1].toUpperCase();
  const bare = text.trim().match(/^[A-Za-z0-9]{8}$/);
  return bare ? bare[0].toUpperCase() : null;
}

export function getPendingInvite(): string | null {
  try { return localStorage.getItem(PENDING_KEY); } catch { return null; }
}
export function setPendingInvite(code: string) {
  try { localStorage.setItem(PENDING_KEY, code); } catch { /* storage off */ }
}
export function clearPendingInvite() {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* storage off */ }
}

/** Web invite page: copy the invite on the store-button tap (must run inside the tap). */
export function copyInviteForInstall(code: string) {
  try { void navigator.clipboard?.writeText(inviteUrl(code)).catch(() => {}); } catch { /* not allowed here */ }
}

/** First open in the app: read the invite back. Must run inside a tap. */
export async function readInviteFromClipboard(): Promise<string | null> {
  try {
    const text = await navigator.clipboard.readText();
    return extractInviteCode(text);
  } catch {
    return null;
  }
}

export type AcceptResult =
  | { ok: true; groupId: string; alreadyMember: boolean }
  | { ok: false; status: number; message: string };

/** Joins the group behind a code. Never throws. */
export async function acceptInvite(code: string): Promise<AcceptResult> {
  try {
    const res = await apiRequest("POST", `/api/invite/${encodeURIComponent(code)}/accept`);
    const data = await res.json();
    queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
    queryClient.invalidateQueries({ queryKey: [`/api/groups/${data.groupId}`] });
    return { ok: true, groupId: data.groupId, alreadyMember: !!data.alreadyMember };
  } catch (err) {
    // apiRequest throws "<status>: <body>"
    const raw = (err as Error)?.message || "";
    const status = Number(raw.split(":")[0]) || 0;
    let message = "That invite didn't work.";
    try { message = JSON.parse(raw.split(": ").slice(1).join(": ")).error || message; } catch { /* keep default */ }
    return { ok: false, status, message };
  }
}
